import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bot, BookOpen, Award, Flame, Target } from "lucide-react";

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [stats, setStats] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    (async () => {
      try { await api.post("/stats/checkin"); } catch (err) { console.warn("[Dashboard] checkin failed:", err?.message); }
      Promise.all([api.get("/courses"), api.get("/progress"), api.get("/stats")]).then(([c,p,s]) => {
        setCourses(c.data); setProgress(p.data); setStats(s.data);
      });
    })();
  }, [user, loading, nav]);

  const checkout = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Deu ruim no checkout"); setCheckoutLoading(false); }
  };

  const totalLessons = courses.reduce((s,c)=> s + (c.lessons?.length||0), 0);
  const completed = progress.reduce((s,p)=> s + (p.completed_lessons?.length||0), 0);

  if (!user) return null;

  return (
    <div data-testid="dashboard" className="grid-bg min-h-[85vh]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <span className="sticker">DASHBOARD</span>
        <h1 className="font-bebas text-6xl md:text-7xl mt-6 tracking-tighter">
          OI, <span className="text-[#CCFF00]">{user.name?.split(" ")[0]?.toUpperCase()}</span>
        </h1>
        <p className="text-white/60 mt-3 font-manrope">Bora manter o ritmo.</p>

        {!user.subscription_active && (
          <div data-testid="dashboard-paywall" className="mt-10 brutal-card brutal-shadow-pink" style={{borderColor:'#FF0055'}}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <span className="sticker">TÁ BLOQUEADO</span>
                <h2 className="font-bebas text-4xl mt-4 tracking-tighter">Libera Geral por R$ 15,99/mês</h2>
                <p className="text-white/70 mt-2 font-manrope">Escolhe a forma de pagamento e destrava tudo.</p>
              </div>
              <div className="flex flex-col gap-3">
                <Link to="/pagar/pix" data-testid="dashboard-pix" className="brutal-btn">Pagar com Pix</Link>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 grid md:grid-cols-4 gap-6">
          <Link to="/missao" data-testid="dash-stats-xp" className="brutal-card block" style={{borderColor:'#CCFF00'}}>
            <Flame className="text-[#FF0055]" size={28}/>
            <div className="font-bebas text-5xl mt-4 text-[#CCFF00]">{stats?.xp ?? 0} XP</div>
            <div className="text-white/60 font-manrope text-sm">Nível {stats?.level ?? 1} • Streak {stats?.streak_days ?? 0}d</div>
          </Link>
          <div className="brutal-card">
            <BookOpen className="text-[#CCFF00]" size={28}/>
            <div className="font-bebas text-5xl mt-4">{courses.length}</div>
            <div className="text-white/60 font-manrope text-sm">Cursos disponíveis</div>
          </div>
          <div className="brutal-card">
            <Award className="text-[#00F0FF]" size={28}/>
            <div className="font-bebas text-5xl mt-4">{completed}/{totalLessons}</div>
            <div className="text-white/60 font-manrope text-sm">Aulas concluídas</div>
          </div>
          <Link to="/coach" data-testid="dash-stats-coach" className="brutal-card block">
            <Bot className="text-[#FF0055]" size={28}/>
            <div className="font-bebas text-5xl mt-4">{user.subscription_active ? "ON" : "OFF"}</div>
            <div className="text-white/60 font-manrope text-sm">Coach AI</div>
          </Link>
        </div>

        <div className="mt-6">
          <Link to="/missao" data-testid="dash-cta-missao" className="brutal-card block" style={{borderColor:'#00F0FF'}}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <Target className="text-[#00F0FF]" size={28}/>
                <h3 className="font-bebas text-3xl mt-3 tracking-tighter">Missões diárias, Radar de Vazamento e Simulador</h3>
                <p className="text-white/60 font-manrope text-sm mt-1">Ganha XP todo dia, caça sangrias na tua grana e simula juros compostos.</p>
              </div>
              <span className="font-bebas text-2xl text-[#00F0FF]">COMEÇAR →</span>
            </div>
          </Link>
        </div>

        <div className="mt-14">
          <h2 className="font-bebas text-4xl tracking-tighter">CONTINUA DE ONDE PAROU</h2>
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {courses.slice(0,4).map((c) => {
              const p = progress.find(x=>x.course_id===c.id);
              const done = p?.completed_lessons?.length || 0;
              const total = c.lessons?.length || 0;
              const pct = total? Math.round((done/total)*100):0;
              return (
                <Link key={c.id} to={`/cursos/${c.id}`} data-testid={`dash-course-${c.id}`} className="brutal-card">
                  <h3 className="font-bebas text-3xl tracking-tighter" style={{color: c.color}}>{c.title}</h3>
                  <div className="mt-4 h-2 bg-white/10">
                    <div className="h-full bg-[#CCFF00]" style={{width: `${pct}%`}}/>
                  </div>
                  <div className="mt-2 text-white/50 font-manrope text-xs uppercase tracking-widest">{done}/{total} • {pct}%</div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
